import {Box, Toolbar, useMediaQuery, useTheme} from '@mui/material';
import React from 'react';
import {Outlet} from 'react-router';
import {navigation} from '../../theme/tokens';
import NavRail from './NavRail';

type AppLayoutProps = {
  children?: React.ReactNode;
};

const AppLayout: React.FC<AppLayoutProps> = ({children}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // State for rail expansion (desktop only)
  const [railExpanded, setRailExpanded] = React.useState(() => {
    // Default to collapsed on tablet, expanded on desktop
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('navRailExpanded');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    }
    return !isTablet;
  });

  // Save preference
  React.useEffect(() => {
    localStorage.setItem('navRailExpanded', JSON.stringify(railExpanded));
  }, [railExpanded]);

  const handleRailToggle = React.useCallback(() => {
    setRailExpanded((prev: boolean) => !prev);
  }, []);

  const railWidth = railExpanded
    ? navigation.railExpandedWidth
    : navigation.railWidth;

  // Mobile layout: content only (AppBar and BottomNav rendered by AppContainer)
  if (isMobile) {
    return (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: {xs: 2, sm: 2},
        }}>
        {children || <Outlet />}
      </Box>
    );
  }

  // Desktop/Tablet layout: NavRail + content
  return (
    <Box sx={{display: 'flex', minHeight: '100vh'}}>
      {/* NavRail */}
      <NavRail expanded={railExpanded} onToggle={handleRailToggle} />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${railWidth}px)`,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}>
        {/* AppBar spacer */}
        <Toolbar />

        {/* Content */}
        <Box sx={{p: {xs: 2, md: 3}}}>{children || <Outlet />}</Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
